import { useCallback, useMemo } from 'react';

import type { EstimationWithDependencies } from '@/hooks/useGetEstimationWithDependencies';
import { useGetEstimationWithDependencies } from '@/hooks/useGetEstimationWithDependencies';

import {
  buildCostRecordsByParentId,
  buildEstimationItemsRecordIndex,
  buildResourceRecordsByParentId,
} from './mappers/estimationItemsTableRecordMapper';
import type { CostEstimationItemsTableRecord, ResourceEstimationItemsTableRecord } from './model';

type Options = {
  shouldFetchResources: boolean;
  shouldFetchEstimationItemCosts: boolean;
};

type CommonOutput = {
  isLoading: boolean;
  data: EstimationWithDependencies | undefined;
  getResourceEstimationItemsTableRecordsByParent: (
    parentEstimationItemId: string | undefined,
  ) => ResourceEstimationItemsTableRecord[];
};

type WithCostItemsMapper = {
  getCostEstimationItemsTableRecordsByParent: (
    parentEstimationItemId: string | undefined,
  ) => CostEstimationItemsTableRecord[];
};

type ReturnType<TOptions extends Options> = CommonOutput &
  (TOptions['shouldFetchEstimationItemCosts'] extends true
    ? WithCostItemsMapper
    : Record<string, never>);

/**
 * Fetches estimation with dependencies like model methodologies and others and most importantly with all estimation items.
 * Optionally also with estimation item resources (for estimation items with purpose RESOURCE)
 * and/or with estimation item costs (for estimation items with purpose COST).
 * If it fetches also estimation item costs, it provides mapper method for estimation items with purpose COST.
 * Estimation items with purpose RESOURCE are fetched regardless of the options arg.
 * @param estimationId
 * @param options Whether to load also estimation items resource and/or estimation item costs
 * @returns
 */
export const useEstimationItemsTableRecords = <T extends Options>(
  estimationId: string,
  options: T,
): ReturnType<T> => {
  const { isLoading, data } = useGetEstimationWithDependencies(estimationId, options);

  // Derive every table record once per data load. `getByParent` below is then an
  // O(1) map lookup instead of re-filtering + re-mapping the whole estimation on
  // every call (which, called recursively per node across the report's tables,
  // was the O(N²)–O(N³) blow-up that froze the browser — EWM-861).
  const index = useMemo(() => (data ? buildEstimationItemsRecordIndex(data) : undefined), [data]);

  const resourceRecordsByParentId = useMemo(
    () => (index ? buildResourceRecordsByParentId(index) : undefined),
    [index],
  );

  const costRecordsByParentId = useMemo(
    () =>
      index && data?.estimationItemCosts !== undefined
        ? buildCostRecordsByParentId(index)
        : undefined,
    [index, data?.estimationItemCosts],
  );

  const getCostEstimationItemsTableRecordsByParent = useCallback(
    (parentEstimationItemId: string | undefined): CostEstimationItemsTableRecord[] =>
      costRecordsByParentId?.get(parentEstimationItemId) ?? [],
    [costRecordsByParentId],
  );

  const getResourceEstimationItemsTableRecordsByParent = useCallback(
    (parentEstimationItemId: string | undefined): ResourceEstimationItemsTableRecord[] =>
      resourceRecordsByParentId?.get(parentEstimationItemId) ?? [],
    [resourceRecordsByParentId],
  );

  const result: CommonOutput = {
    isLoading: isLoading,
    data,
    getResourceEstimationItemsTableRecordsByParent,
  };

  if (options.shouldFetchEstimationItemCosts) {
    (result as CommonOutput & WithCostItemsMapper).getCostEstimationItemsTableRecordsByParent =
      getCostEstimationItemsTableRecordsByParent;
  }

  return result as ReturnType<T>;
};

export const useCostEstimationItemsTableRecords = (estimationId: string) =>
  useEstimationItemsTableRecords(estimationId, {
    shouldFetchResources: false,
    shouldFetchEstimationItemCosts: true,
  });

export const useResourceEstimationItemsTableRecords = (
  estimationId: string,
  { shouldFetchResources }: Pick<Options, 'shouldFetchResources'>,
) =>
  useEstimationItemsTableRecords(estimationId, {
    shouldFetchResources,
    shouldFetchEstimationItemCosts: false,
  });
